import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { query, writeQuery } from '@confluence/query-preloader-tools/entry-points/graphql';
import { expValEquals } from '@confluence/feature-experiments';

import { ShareAndRestrictButtonQuery } from '../graphql/ShareAndRestrictButtonQuery.graphql';
import { ShareAndRestrictButtonSpaceOverviewQuery } from '../graphql/ShareAndRestrictButtonSpaceOverviewQuery.graphql';
import type {
	ShareAndRestrictButtonSpaceOverviewQuery as ShareAndRestrictButtonSpaceOverviewQueryType,
	ShareAndRestrictButtonSpaceOverviewQueryVariables,
} from '../graphql/__types__/ShareAndRestrictButtonSpaceOverviewQuery';
import type {
	ShareAndRestrictButtonQuery as ShareAndRestrictButtonQueryType,
	ShareAndRestrictButtonQueryVariables,
} from '../graphql/__types__/ShareAndRestrictButtonQuery';

function transformSpaceHomepageData(data: any) {
	return {
		singleContent: data?.spaceHomepage || null,
		publicLinkPage: data?.confluence_publicLinkSpaceHomePage || null,
	};
}

export async function preloadShareAndRestrictButtonQuery(
	contentId?: string,
	spaceKey?: string,
): Promise<
	| {
			data: ShareAndRestrictButtonSpaceOverviewQueryType | ShareAndRestrictButtonQueryType | null;
			hasErrors: boolean;
	  }
	| undefined
> {
	const { isLoggedIn, cloudId } = getPreloaderFnContext();

	if (!isLoggedIn) {
		return;
	}

	if (
		!contentId &&
		spaceKey &&
		expValEquals('fix_preload_share_and_restrict_query_chaining_', 'cohort', 'test')
	) {
		const result = await query<
			ShareAndRestrictButtonSpaceOverviewQueryType,
			ShareAndRestrictButtonSpaceOverviewQueryVariables
		>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: ShareAndRestrictButtonSpaceOverviewQuery,
			variables: { cloudId, spaceKey },
			errorPolicy: 'all',
		});

		if (result.hasErrors) {
			return result;
		}

		const data = result.data;

		const homepageId = data?.spaceHomepage?.id;
		if (!homepageId) {
			return;
		}

		const transformedData = transformSpaceHomepageData(data);

		// Prime Apollo cache so the button component finds its data already cached
		writeQuery<ShareAndRestrictButtonQueryType, ShareAndRestrictButtonQueryVariables>({
			query: ShareAndRestrictButtonQuery,
			variables: {
				contentId: homepageId,
				isContentTypePublicLinkEligible: true,
			},
			data: transformedData,
		});

		return;
	}

	if (contentId) {
		const result = await query<
			ShareAndRestrictButtonQueryType,
			ShareAndRestrictButtonQueryVariables
		>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: ShareAndRestrictButtonQuery,
			variables: {
				contentId,
				isContentTypePublicLinkEligible: true,
			},
			errorPolicy: 'all',
		});

		if (result.hasErrors) {
			return result;
		}

		return;
	}

	// eslint-disable-next-line no-console
	console.error('Either contentId or spaceKey must be provided');
	return;
}
